import { reactive } from 'vue'

const VOICE_KEY = 'wortgarten-de-voice-uri'
const supported = typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window

export const germanSpeech = reactive({
  available: supported,
  loaded: false,
  voices: [],
  selectedUri: supported ? localStorage.getItem(VOICE_KEY) || '' : '',
})

function germanOnly(voices) {
  return voices.filter((voice) => /^de(?:-|$)/i.test(voice.lang || ''))
}

function score(voice) {
  const name = `${voice.name || ''} ${voice.voiceURI || ''}`.toLowerCase()
  // iOS does not expose Apple’s quality enum through the Web Speech API. A
  // previously user-selected system voice is always preferred; otherwise keep
  // playback local and favor named natural/enhanced voices when available.
  return (voice.localService ? 100 : 0)
    + (/premium|enhanced|hochwertig|natural|siri/.test(name) ? 20 : 0)
    + (/^de-de$/i.test(voice.lang || '') ? 8 : 0)
    + (voice.default ? 4 : 0)
}

export function refreshGermanVoices() {
  if (!supported) return []
  const voices = germanOnly(window.speechSynthesis.getVoices())
    .sort((left, right) => score(right) - score(left) || left.name.localeCompare(right.name, 'de'))
  germanSpeech.voices = voices
  germanSpeech.loaded = true
  if (germanSpeech.selectedUri && !voices.some((voice) => voice.voiceURI === germanSpeech.selectedUri)) {
    germanSpeech.selectedUri = ''
    localStorage.removeItem(VOICE_KEY)
  }
  return voices
}

export function initGermanSpeech() {
  if (!supported) return
  refreshGermanVoices()
  window.speechSynthesis.addEventListener('voiceschanged', refreshGermanVoices)
}

export function selectGermanVoice(uri) {
  germanSpeech.selectedUri = uri || ''
  if (germanSpeech.selectedUri) localStorage.setItem(VOICE_KEY, germanSpeech.selectedUri)
  else localStorage.removeItem(VOICE_KEY)
}

export function preferredGermanVoice() {
  const voices = germanSpeech.voices.length ? germanSpeech.voices : refreshGermanVoices()
  return voices.find((voice) => voice.voiceURI === germanSpeech.selectedUri) || voices[0] || null
}

export function stopGermanSpeech() {
  if (supported) window.speechSynthesis.cancel()
}

export function speakGerman(text, options = {}) {
  if (!supported || !text) return null
  const utterance = new SpeechSynthesisUtterance(text)
  const voice = preferredGermanVoice()
  utterance.lang = voice?.lang || 'de-DE'
  if (voice) utterance.voice = voice
  utterance.rate = options.rate ?? .9
  utterance.pitch = options.pitch ?? 1
  utterance.volume = options.volume ?? 1
  if (options.onstart) utterance.onstart = options.onstart
  if (options.onend) utterance.onend = options.onend
  if (options.onerror) utterance.onerror = options.onerror
  window.speechSynthesis.cancel()
  window.speechSynthesis.speak(utterance)
  return utterance
}

export function speechUnavailableMessage() {
  return '未发现德语系统声音。请在 iPhone「设置 → 辅助功能 → 朗读内容 → 声音 → 德语（德国）」下载高品质声音后重试。'
}
